import { afterNextRender, Component, HostListener, signal } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs';
import { environment } from '../../../../../environments/environment';
import { APP_PROD_VERSION, APP_VERSION } from '../../../../core/constants/app.constant';
import { GROUP_ID_URL_PATTERN } from '../../../../core/constants/general.constant';
import { CreatePostPopupComponent } from '../../../../core/libraries/popup/components/create-post-popup/create-post-popup.component';
import { CreatePostEventService } from '../../../../core/services/helper/create-post-event/create-post-event.service';
import { GroupContextService } from '../../../../core/services/helper/group-context/group-context.service';

@Component({
    selector: 'app-sidebar',
    standalone: false,
    templateUrl: './sidebar.component.html',
    styleUrl: './sidebar.component.scss',
})
export class SidebarComponent {
    protected APP_VERSION = APP_VERSION;
    protected APP_PROD_VERSION = APP_PROD_VERSION;
    protected envName = environment.name;
    protected isOpen = true;
    protected isMobile = false;
    protected showCreatePost = signal<boolean>(true);
    protected groupId!: number;
    protected isGroup = false;
    protected isJoinedGroup = false;
    protected isCommunity = false;

    constructor(
        private router: Router,
        private dialog: MatDialog,
        private groupContextService: GroupContextService,
        private createPostEventService: CreatePostEventService,
    ) {
        afterNextRender(() => {
            this.checkScreen();
            this.groupContextService.groupDetails$.subscribe((details) => {
                this.groupId = details.id;
                this.isJoinedGroup = !!details?.joined;
                this.updateShowCreatePost(); // recompute when joined status changes
            });
            this.updateContext(this.router.url);
            this.router.events
                .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
                .subscribe(() => this.updateContext(this.router.url));
        });
    }

    @HostListener('window:resize')
    onResize(): void {
        this.checkScreen();
    }

    toggleSidebar(): void {
        this.isOpen = !this.isOpen;
        if (this.isMobile) {
            if (this.isOpen) {
                // Disable background scroll
                document.body.style.overflow = 'hidden';
            } else {
                // Re-enable scroll
                document.body.style.overflow = '';
            }
        }
    }

    isActive(route: string): boolean {
        return this.router.url === route;
    }

    protected openCreatePostPopup(): void {
        const popupConfig = CreatePostPopupComponent.getDialogConfig();
        popupConfig.maxWidth = '768px';
        popupConfig.data = { isGroup: this.isGroup, groupId: this.groupId };
        const dialogRef = this.dialog.open(CreatePostPopupComponent, popupConfig);
        dialogRef.afterClosed().subscribe((result) => {
            if (result?.type === 'CREATE_POST_OPTIMISTIC') {
                this.createPostEventService.emitOptimisticPost(result.payload);
            }
        });

        if (!this.isOpen) {
            return;
        }
        this.toggleSidebar();
    }

    private checkScreen(): void {
        this.isMobile = window.innerWidth < 576;
        this.isOpen = !this.isMobile;
    }

    private updateContext(url: string): void {
        this.isGroup = GROUP_ID_URL_PATTERN.test(url);
        this.isCommunity = url.includes('/community');
        this.updateShowCreatePost();
    }

    private updateShowCreatePost(): void {
        const shouldShow = (this.isGroup && this.isJoinedGroup) || this.isCommunity;
        this.showCreatePost.set(shouldShow);
    }
}
